// One-time live-location request, shown on the first authenticated session on
// this device. "Allow" hands off to the real browser/OS permission prompt —
// nothing is bypassed. The answer is remembered so the guard is never asked
// again; it stays changeable from the Time Clock screen.
import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useLiveLocationConsent } from "@/lib/live-location-consent";
import { useProfileLocationConsent } from "@/lib/location-consent-profile";

export function LiveLocationConsentDialog() {
  const { user, currentOrg } = useAuth();
  const [consent, setConsent] = useLiveLocationConsent(user?.id ?? null);
  // The account-level disclosure comes first; only one prompt shows at a time.
  const { status: profileConsent } = useProfileLocationConsent(user?.id ?? null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id || !currentOrg || profileConsent !== "given") { setOpen(false); return; }
    // Slight delay so it never competes with the first paint of the shell.
    const t = window.setTimeout(() => setOpen(consent === "unset"), 1200);
    return () => window.clearTimeout(t);
  }, [user?.id, currentOrg, consent, profileConsent]);

  if (!open) return null;

  const allow = async () => {
    setBusy(true);
    try {
      if (typeof navigator !== "undefined" && navigator.geolocation) {
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(),
            () => resolve(),
            { enableHighAccuracy: true, timeout: 10_000 },
          );
        });
      }
    } finally {
      setConsent("granted");
      setBusy(false);
      setOpen(false);
    }
  };

  const decline = () => {
    setConsent("declined");
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mr-loc-consent-title"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 sm:items-center"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#C0C0C0]/20 bg-card p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-full bg-primary/15 p-2 text-primary"><MapPin className="h-5 w-5" /></span>
          <div>
            <h2 id="mr-loc-consent-title" className="font-display text-lg font-bold">
              Share your location while on duty?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              When you are clocked in, your position is shown on your supervisor's live map so
              they can see coverage and reach you in an emergency. It is never shared with other
              guards, and reporting stops the moment you clock out.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <Button onClick={allow} disabled={busy} className="sm:min-w-32">
            {busy ? "Requesting…" : "Allow"}
          </Button>
          <Button variant="outline" onClick={decline} disabled={busy}>Not now</Button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          You'll only be asked once. You can change this any time on the Time Clock screen.
        </p>
      </div>
    </div>
  );
}
